/* eslint-disable jsx-a11y/alt-text */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import wavesImg from 'assets/images/waves.svg';
import { conlicitaGo } from 'services/actions/conlicita_go';
import Loader from 'components/atoms/loader';
import { openSupportChat } from 'services/support_chat';
import { Container } from './styles';

// O app de cursos avisa por aqui que a sessão dele venceu. O token nasce no
// nosso servidor, então só nós conseguimos emitir outro — recarregar o iframe
// não adiantaria, o src carrega o mesmo token vencido.
const REAUTH_NEEDED = 'conlicitago:reauth-needed';

// Trava de segurança: se o token novo também vier recusado, o app pediria de
// novo e entraríamos em laço de renovação.
const INTERVALO_MIN_RENOVACAO_MS = 10000;

const ConlicitaGo = () => {
  const [page, setPage] = useState<string>('');
  const frameRef = useRef<HTMLIFrameElement>(null);
  const ultimaRenovacao = useRef<number>(0);

  /**
   * Encaixa o iframe no espaço entre o header e a base da janela.
   *
   * Não dá para usar `calc(100vh - 56px)` fixo: o header é `fixed-top` com
   * `min-height: 56px`, mas cresce com avatar/botões, enquanto o
   * `.page-container` reserva sempre 56px. Quando o header fica mais alto que
   * isso, ele passa por cima do topo do iframe (o primeiro ícone da sidebar
   * some) e a conta da altura erra pela mesma diferença.
   *
   * Medindo a borda INFERIOR real do header (`.fixed-top`), empurramos o
   * iframe para baixo do que sobra e ajustamos a altura na mesma medida —
   * correto para qualquer altura de header e a cada resize.
   */
  const fitFrame = useCallback(() => {
    const el = frameRef.current;
    if (!el) return;

    // Pode haver mais de um `.fixed-top` empilhado; vale a borda mais baixa.
    const headerBottom = Array.from(document.querySelectorAll<HTMLElement>('.fixed-top')).reduce(
      (maior, node) => Math.max(maior, node.getBoundingClientRect().bottom),
      0,
    );

    // O deslocamento vai como PADDING no wrapper, não como margem no iframe:
    // `.page-container` já tem `margin-top`, e sem borda/padding a margem do
    // primeiro filho colapsaria com a dele — o ajuste simplesmente não valeria.
    const wrapper = el.parentElement;
    if (wrapper) wrapper.style.paddingTop = '0px';

    // Mede a posição natural (com o padding zerado) para não acumular a cada
    // chamada.
    const topoLayout = el.getBoundingClientRect().top;
    const desloca = Math.max(0, headerBottom - topoLayout);

    if (wrapper) wrapper.style.paddingTop = `${desloca}px`;
    el.style.height = `${Math.max(window.innerHeight - topoLayout - desloca, 320)}px`;
  }, []);

  useEffect(() => {
    fitFrame();
    window.addEventListener('resize', fitFrame);
    return () => window.removeEventListener('resize', fitFrame);
  }, [page, fitFrame]);

  // O app de cursos mantém a identidade escura do GO, e o header da plataforma
  // acompanha (ver header/styles.ts). Só nesta página: ao sair, o header volta
  // às cores da plataforma.
  useEffect(() => {
    document.documentElement.setAttribute('data-tema', 'go');
    return () => document.documentElement.removeAttribute('data-tema');
  }, []);

  /**
   * Busca uma URL nova (com token novo) e troca o src do iframe. Trocar só o
   * src remonta o app de cursos já autenticado, sem recarregar a plataforma
   * inteira nem tirar o usuário da página.
   */
  const renovarSessao = useCallback(async () => {
    const agora = Date.now();
    if (agora - ultimaRenovacao.current < INTERVALO_MIN_RENOVACAO_MS) return;
    ultimaRenovacao.current = agora;

    const fetchedPage = await conlicitaGo();
    const url = fetchedPage?.data?.url_courses;
    if (url) setPage(url);
  }, []);

  useEffect(() => {
    const aoReceberMensagem = (event: MessageEvent) => {
      if (event.data?.type !== REAUTH_NEEDED) return;
      // Só aceita de quem está dentro do nosso iframe, e da origem que nós
      // mesmos apontamos no src. O guard de 'sale' também protege o
      // new URL(): 'sale' não é URL e o parse lançaria dentro do handler.
      if (!page || page === 'sale' || event.origin !== new URL(page).origin) return;
      if (event.source !== frameRef.current?.contentWindow) return;
      renovarSessao();
    };

    window.addEventListener('message', aoReceberMensagem);
    return () => window.removeEventListener('message', aoReceberMensagem);
  }, [page, renovarSessao]);

  useEffect(() => {
    (async () => {
      const fetchedPage = await conlicitaGo();
      if (fetchedPage?.status === 400) {
        setPage('sale');
      } else {
        // Antes daqui saía um `window.location.href`, que tirava o usuário da
        // plataforma. Agora a URL vira o src de um iframe: o aluno continua em
        // conlicitacao.com.br, com o header e a navegação daqui por volta.
        setPage(fetchedPage?.data?.url_courses);
      }
    })();
    document.title = 'ConLicitaGo - ConLicitação';
  }, []);

  const renderSalePage = () => (
    <div className="row">
      <div className="col-12">
        <div className={`card card-shadow p-3 p-md-4 `}>
          <div className="active-services-bg">
            <div className="row align-items-center m-0">
              <div className="col-12 col-md-6">
                <h4 className="mb-0 px-md-3 px-4 py-md-0 py-5">
                  Melhore sua experiência com o ConLicitação através dos nossos novos serviços
                </h4>
              </div>
              <div className="col-12 col-md-6 p-0">
                <div className="active-services-wave">
                  <img className="active-services-wave-img d-none d-lg-block" src={wavesImg} />
                  <div className="active-services-wave-bg">
                    <h4 className="text-white mb-0">
                      Conheça o ConLicitaGo - o primeiro e único streaming de licitações públicas do
                      país!
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row align-items-center">
            <div className="col-12 col-md-6 mt-md-0 mt-5 text-center">
              <iframe
                width="100%"
                height="315"
                src="https://player.vimeo.com/video/788793022"
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
            <div className="col-12 col-md-6 mt-5">
              <h4 className="text-center mb-3 font-weight-bold">ConLicita Go</h4>
              <p>
                Nele reunimos os melhores conteúdos de licitação para você assistir quando e onde
                quiser, no seu tempo e ritmo!
              </p>
              <p>Já temos mais de 100 horas de conteúdo para você assistir livremente!</p>
              <ul className="list-with-check mt-2 mb-4">
                <li>
                  <span className="oi oi-check pr-2" />
                  Você e seu time assistem onde quiserem nas horas mais convenientes
                </li>
                <li>
                  <span className="oi oi-check pr-2" />
                  Conteúdo desenvolvido com base nas últimas atualizações oficiais do governo
                </li>
                <li>
                  <span className="oi oi-check pr-2" />
                  Acesso individualizado para até 5 membros do seu time
                </li>
                <li>
                  <span className="oi oi-check pr-2" />
                  Certificado de conclusão individual e válido em todo o território nacional{' '}
                </li>
              </ul>
              <button
                type="button"
                className="mt-3 btn btn-primary btn-lg btn-block"
                onClick={() => {
                  openSupportChat();
                }}
              >
                Clique e fale com um consultor pelo chat
              </button>
              <span className="mt-2">
                Se preferir entre em contato conosco pelo telefone e whatsapp +55 (11) 3789-9102 ou
                pelo e-mail
              </span>
              <a href="mailto:comercial@conlicitacao.com.br" className="text-center">
                comercial@conlicitacao.com.br
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Com acesso liberado, o app de cursos é embarcado; sem acesso, cai na página
  // de venda. O iframe fica fora do `.container` para ocupar a largura toda.
  if (page !== '' && page !== 'sale') {
    return (
      <Container>
        <iframe
          ref={frameRef}
          className="conlicitago-frame"
          src={page}
          title="ConLicitaGo"
          allow="fullscreen; autoplay; picture-in-picture; clipboard-write"
          allowFullScreen
          onLoad={fitFrame}
        />
      </Container>
    );
  }

  return (
    <Container>
      <div className="container p-4 mt-5">{page === '' ? <Loader /> : renderSalePage()}</div>
    </Container>
  );
};

export default ConlicitaGo;
